/**
 * Утилиты для работы с данными пользователя и обновления UI
 */

const ERRORBORN_STATUS_CACHE_TTL_MS = 30000; // 30 секунд

/**
 * Обновляет UI балансов пользователя (монеты и звёзды)
 */
export function updateUserBalancesUI(currentUser, documentRef = document) {
  if (!currentUser) return;
  
  const coins = currentUser.coins || 0;
  const stars = currentUser.stars || 0;
  
  // Обновляем все элементы с балансами (для совместимости со старым и новым UI)
  const coinsSelectors = [
    "coins", "coins-header", 
    "coins-store", "coins-collection", "coins-gifts", "coins-send-gift", 
    "coins-battle", "coins-achievements", "coins-pass", "coins-club",
    "coins-inventory", "coins-energy", "coins-glitchlings",
    "coins-payment", "coins-royalty-info", "coins-transactions", 
    "coins-royalty-stats", "coins-creature-details", "coins-professions",
    "coins-upgrades", "coins-upgrade-history", "coins-admin",
    "glitchlings-coins"
  ];
  const starsSelectors = [
    "stars", "stars-header",
    "stars-store", "stars-collection", "stars-gifts", "stars-send-gift",
    "stars-battle", "stars-achievements", "stars-pass", "stars-club",
    "stars-inventory", "stars-energy", "stars-glitchlings",
    "stars-payment", "stars-royalty-info", "stars-transactions",
    "stars-royalty-stats", "stars-creature-details", "stars-professions",
    "stars-upgrades", "stars-upgrade-history", "stars-admin"
  ];
  
  coinsSelectors.forEach(id => {
    const el = documentRef.getElementById(id);
    if (el) {
      el.textContent = coins;
    }
  });
  
  starsSelectors.forEach(id => {
    const el = documentRef.getElementById(id);
    if (el) {
      el.textContent = stars;
    }
  });
  
  // Обновляем новый Glitchlings интерфейс
  updateGlitchlingsHomeUI(currentUser, documentRef);
}

/**
 * Обновляет UI главного экрана в стиле Glitchlings
 */
function updateGlitchlingsHomeUI(user, documentRef = document) {
  if (!user) return;
  
  // Профиль
  const profileName = documentRef.getElementById('glitchlings-profile-name');
  if (profileName) {
    const telegramWebApp = window.Telegram?.WebApp;
    const firstName = telegramWebApp?.initDataUnsafe?.user?.first_name || user.username || 'GLITCHLING';
    profileName.textContent = firstName.toUpperCase();
  }
  
  // Трофеи (используем звёзды как трофеи)
  const trophies = documentRef.getElementById('glitchlings-trophies');
  if (trophies) {
    trophies.textContent = user.stars || 0;
  }
  
  // Победы
  const wins = documentRef.getElementById('glitchlings-wins');
  if (wins) {
    wins.textContent = user.wins || 0;
  }
  
  const glitchlingsCoins = documentRef.getElementById('glitchlings-coins');
  if (glitchlingsCoins) {
    glitchlingsCoins.textContent = user.coins || 0;
  }
  
  // Энергия (общая энергия всех существ)
  const power = documentRef.getElementById('glitchlings-power');
  if (power) {
    power.textContent = user.totalEnergy || 0;
  }
  
  // Прогресс (можно использовать прогресс пасса или другую метрику)
  const progressBar = documentRef.querySelector('.glitchlings-progress-bar[role="progressbar"]');
  const progressFill = documentRef.getElementById('glitchlings-progress-fill');
  const progressText = documentRef.getElementById('glitchlings-progress-text');
  if (progressBar && progressFill && progressText) {
    const current = user.stars || 0;
    const max = 3800;
    const percent = Math.min(100, (current / max) * 100);
    progressFill.style.width = `${percent}%`;
    progressText.textContent = `${current}/${max}`;
    // Обновляем ARIA-атрибуты для доступности
    progressBar.setAttribute('aria-valuenow', current);
    progressBar.setAttribute('aria-valuetext', `${current} из ${max} трофеев`);
  }
  
  // XP (можно использовать опыт или другую метрику)
  const xpBar = documentRef.querySelector('.glitchlings-xp-bar[role="progressbar"]');
  const xpFill = documentRef.getElementById('glitchlings-xp-fill');
  const xpCurrent = documentRef.getElementById('glitchlings-xp-current');
  const xpMax = documentRef.getElementById('glitchlings-xp-max');
  if (xpBar && xpFill && xpCurrent && xpMax) {
    const current = (user.stars || 0) % 950;
    const max = 950;
    const percent = (current / max) * 100;
    xpFill.style.width = `${percent}%`;
    xpCurrent.textContent = current;
    xpMax.textContent = max;
    // Обновляем ARIA-атрибуты для доступности
    xpBar.setAttribute('aria-valuenow', current);
    xpBar.setAttribute('aria-valuetext', `${current} из ${max} опыта`);
  }
  
  // Персонаж:
  // 1) если есть глобальный override (выбор в 1v1) — используем его
  // 2) иначе берём первого из коллекции или дефолтного
  const collection = user.collection || [];
  const overrideCreature = window.__homeScreenCreatureOverride || null;
  const firstCreature = overrideCreature || collection[0] || { name: 'BITCOR', visual: '🦉', level: 1 };
  
  const characterName = documentRef.getElementById('glitchlings-character-name');
  if (characterName) {
    characterName.textContent = firstCreature.name?.toUpperCase() || 'BITCOR';
  }
  
  const characterVisual = documentRef.getElementById('glitchlings-character-visual');
  const characterImage = documentRef.getElementById('glitchlings-character-image');
  const characterEmoji = documentRef.getElementById('glitchlings-character-emoji');
  
  if (characterVisual) {
    // Получаем ID существа для изображения
    const creatureId = firstCreature.creatureId || firstCreature.creature_id || firstCreature.id || 1;
    const imageUrl = firstCreature.imageUrl || firstCreature.image_url || `/creatures/${creatureId}.png`;
    
    // Если есть изображение, показываем его, иначе показываем эмодзи
    if (characterImage && characterEmoji) {
      // Устанавливаем изображение
      characterImage.src = imageUrl;
      characterImage.alt = firstCreature.name || 'Glitchling';
      characterImage.style.display = 'block';
      // Убираем фиксированную высоту, чтобы работал адаптивный размер
      characterImage.style.height = 'auto';
      characterImage.style.width = '';
      characterEmoji.style.display = 'none';
      
      // Обработка ошибки загрузки изображения
      characterImage.onerror = function() {
        console.warn('[updateGlitchlingsHomeUI] Изображение не загрузилось:', imageUrl);
        this.style.display = 'none';
        if (characterEmoji) {
          characterEmoji.textContent = firstCreature.visual || '🦉';
          characterEmoji.style.display = 'block';
        }
      };
      
      // Обработка успешной загрузки
      characterImage.onload = function() {
        console.log('[updateGlitchlingsHomeUI] Изображение загружено:', imageUrl);
        this.style.display = 'block';
        if (characterEmoji) {
          characterEmoji.style.display = 'none';
        }
      };
    } else {
      // Fallback для старого кода
      characterVisual.textContent = firstCreature.visual || '🦉';
    }
  }
  
  const characterLevel = documentRef.getElementById('glitchlings-character-level-value');
  if (characterLevel) {
    characterLevel.textContent = firstCreature.level || 1;
  }
  
  const powerLevel = documentRef.getElementById('glitchlings-power-level');
  if (powerLevel) {
    powerLevel.textContent = firstCreature.level || 1;
  }
  
  // Ранг (можно использовать редкость)
  const rankLevel = documentRef.getElementById('glitchlings-rank-level');
  if (rankLevel) {
    const rarity = firstCreature.rarity || 'common';
    const rankMap = { common: 'I', rare: 'II', epic: 'III', legendary: 'IV' };
    rankLevel.textContent = rankMap[rarity] || 'I';
  }
  
  // Показ кнопки админ-панели для всех админов (проверка через API)
  const adminButton = documentRef.getElementById('admin-button-home');
  if (adminButton) {
    // Используем глобальную функцию checkAdminStatus, если она доступна
    // Иначе проверяем через whitelist (если пользователь в whitelist и is_admin = true)
    if (typeof window.checkAdminStatus === 'function') {
      // Асинхронная проверка через API
      window.checkAdminStatus().then(isAdmin => {
        adminButton.style.display = isAdmin ? 'block' : 'none';
      }).catch(() => {
        adminButton.style.display = 'none';
      });
    } else {
      // Fallback: скрываем кнопку, если функция проверки недоступна
      adminButton.style.display = 'none';
    }
  }
}

/**
 * Создаёт менеджер для загрузки данных главного экрана
 */
export function createHomeScreenLoader({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  getCurrentUser,
  setCurrentUser,
  updateUserBalancesUI,
  fetchProfessionsData,
  renderCollectionPreview,
  loadAchievementsStats,
  fetchErrorbornStatus,
  checkAdminStatus,
}) {
  /**
   * Загружает данные главного экрана (пользователь, коллекция, достижения, Errorborn)
   */
  async function loadHomeScreenData() {
    const telegramWebApp = getTelegramWebApp();
    if (!telegramWebApp || !telegramWebApp.initData) {
      setTimeout(() => {
        const app = getTelegramWebApp();
        if (app && app.initData) {
          loadHomeScreenData();
        }
      }, 1000);
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/user/me`, {
        headers: buildAuthHeaders(),
      });

      if (response.success && response.data.success) {
        const user = response.data.user;
        setCurrentUser(user);
        updateUserBalancesUI(user);

        try {
          await fetchProfessionsData();
        } catch (error) {
          console.warn(
            "Не удалось загрузить список профессий:",
            error?.message || error,
          );
        }

        const collection = response.data.collection || [];
        renderCollectionPreview(collection);
        loadAchievementsStats();
        fetchErrorbornStatus(true); // force refresh
        loadOnlineUsersCount(); // Загружаем количество онлайн пользователей
        
        // Обновляем данные для нового Glitchlings интерфейса
        updateGlitchlingsHomeUI({ ...user, collection });
      }
    } catch (error) {
      console.error("Load home error:", error);
    }
  }

  /**
   * Загружает и отображает количество онлайн пользователей
   */
  async function loadOnlineUsersCount() {
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/online/count?minutes=15`, {
        headers: buildAuthHeaders(),
      });

      if (response.success && response.data.success) {
        const count = response.data.count || 0;
        const onlineEl = document.getElementById("online-users-count");
        if (onlineEl) {
          onlineEl.textContent = count;
        }
        // Обновляем новый интерфейс
        const glitchlingsOnlineCount = document.getElementById("glitchlings-online-count");
        if (glitchlingsOnlineCount) {
          glitchlingsOnlineCount.textContent = count;
        }
      }
    } catch (error) {
      console.warn("Load online users count error:", error);
      // Устанавливаем значение по умолчанию при ошибке
      const onlineEl = document.getElementById("online-users-count");
      if (onlineEl) {
        onlineEl.textContent = "?";
      }
      const glitchlingsOnlineCount = document.getElementById("glitchlings-online-count");
      if (glitchlingsOnlineCount) {
        glitchlingsOnlineCount.textContent = "?";
      }
    }
  }

  return {
    loadHomeScreenData,
    loadOnlineUsersCount,
  };
}










